import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Button,
  Alert,
  RefreshIcon,
  HomeIcon,
  ExclamationCircleIcon,
} from '../ui';
import { formatErrorMessage } from '../../utils/errorFormatting';
import Logger from '../../utils/logger';

interface Props {
  children: ReactNode;
  /** Custom fallback component to render when an error occurs */
  fallback?: ReactNode;
  /** Whether to show technical error details (for development) */
  showDetails?: boolean;
  /** Custom title for the error boundary */
  title?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire application
 */
class ErrorBoundary extends Component<Props, State> {
  private logger = Logger.create('ErrorBoundary');

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error details
    this.logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // In production, you might want to log this to an error reporting service
  }

  handleReload = () => {
    // Reload the page to recover from the error
    window.location.reload();
  };

  handleGoHome = () => {
    // Navigate to home page
    window.location.href = '/';
  };

  handleReset = () => {
    // Reset the error boundary state to try rendering again
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const formatted = formatErrorMessage(this.state.error, 'unknown');
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="container mx-auto max-w-2xl px-4 my-8">
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <ExclamationCircleIcon className="w-16 h-16 text-error-600 mb-4" />

            <h1 className="text-2xl font-bold text-secondary-900 mb-4">
              {this.props.title || 'Something went wrong'}
            </h1>

            <Alert
              variant="error"
              title="Application Error"
              className="w-full max-w-xl mb-6 text-left"
            >
              <p className="mb-2">{formatted.message}</p>
              {formatted.actionable && (
                <p className="text-sm opacity-80">{formatted.actionable}</p>
              )}
            </Alert>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button
                variant="primary"
                size="lg"
                onClick={this.handleReload}
                leftIcon={<RefreshIcon className="w-5 h-5" />}
              >
                Reload Page
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={this.handleGoHome}
                leftIcon={<HomeIcon className="w-5 h-5" />}
              >
                Go to Home
              </Button>

              {isDevelopment && (
                <Button variant="ghost" size="lg" onClick={this.handleReset}>
                  Try Again
                </Button>
              )}
            </div>

            {/* Show technical details in development mode or if explicitly requested */}
            {(isDevelopment || this.props.showDetails) && this.state.error && (
              <Alert
                variant="warning"
                title="Technical Details (Development Only)"
                className="w-full max-w-3xl text-left"
              >
                <div className="mt-2">
                  <p className="font-semibold text-sm mb-1">Error Message:</p>
                  <pre className="whitespace-pre-wrap font-mono text-xs mb-4 p-2 bg-secondary-100 rounded">
                    {this.state.error.toString()}
                  </pre>

                  {this.state.errorInfo && (
                    <>
                      <p className="font-semibold text-sm mb-1">
                        Component Stack:
                      </p>
                      <pre className="whitespace-pre-wrap font-mono text-xs p-2 bg-secondary-100 rounded">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </Alert>
            )}
          </div>
        </div>
      );
    }

    // Normal rendering when no error
    return this.props.children;
  }
}

export default ErrorBoundary;
